import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F3800PIIIConsolService } from './f3800-piii-consol.service';
import { NotificationService } from '@appkit4/angular-components/notification';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';

@Component({
    selector: 'gtw-f3800-piii-consol',
    templateUrl: './f3800-piii-consol.component.html',
    styleUrls: ['./f3800-piii-consol.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F3800PIIIConsolComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  filterParams: any = {};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  // Row data from AngularJS (similar to F8916 pattern)
  rowData: any = {};
  selectedRows: any[] = [];

  private isInitialized = false;

  // Component properties migrated from CreditsPartIIIConsolCtrl.js
  modalTitle = 'Edit Credit Forms';
  userSettings: any;
  logged_in_user: string = '';
  defaultsMultipleSelected: any[] = [];
  gridData: any = {};
  curentyear: string = '';
  isSaveClicked = false;
  crudLoading = false;
  minimise = false;
  currentTabName = "Form Entry";
  
  // Form data structures
  formDataObj: any = {};
  formCalcData: any[] = [];
  list: any[] = [];
  formList: any[] = [];
  tempCheckBoxList: any[] = [];
  tableHeader: any[] = [];
  formHeader: any[] = [];
  outerList: any[] = [];
  
  // Batch processing arrays
  allTabsFormHeader: any[] = [];
  allTabsTableHeader: any[] = [];
  allTabsList: any[] = [];
  allTablsOutterList: any[] = [];
  originalTabsList: any[] = [];
  originalTablsOutterList: any[] = [];
  
  // Current data and state
  activeTab: string = '';
  loadingData = true;
  fm: string = '';
  sn: string = '';
  pn: string = '';
  sm: string = '';
  currentTabs: string[] = [];
  batchSelect = false;
  
  // Maps for form headers
  subFormHeaderList = new Map();
  subTableHeaderList = new Map();
  
  // LOV data
  formLOV: any[] = [];

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f3800PIIIConsolService: F3800PIIIConsolService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private _notificationSvc: NotificationService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  private showSpecialFormNotice(): void {
    const formNotices: Record<string, string> = {
      'F8912': 'f3800-f8912-info',
      'F8820': 'f3800-f8820-info',
      'F8941': 'f3800-f8941-info',
    };
    const noticeId = formNotices[this.activeTab];
    if (noticeId) {
      this.cdr.detectChanges();
      setTimeout(() => {
        this._notificationSvc.show('', `${this.activeTab} Form: Specific template for ${this.activeTab} would be implemented here with its unique structure and fields.`, '', '', { id: noticeId, duration: 0, showClose: false });
      }, 0);
    }
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRowPayload = rowPayloadFromActiveScreen(activeScreen);
    this.selectedRows = Array.isArray(rawRowPayload) ? rawRowPayload : [];
    this.rowData = firstGridRowObjectFromPayload(rawRowPayload);
    this.isInitialized = false;
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }

      if (this.isInitialized) {
        return;
      }

      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';

      if (!this.hasRequiredRowData()) {
        return;
      }

      this.isInitialized = true;
      this.setupComponentData();
      this.initializeComponent();
    } catch (e) {
      console.error('Error in F3800PIIIConsol init:', e);
    }
  }

  private hasRequiredRowData(): boolean {
    return !!(
      this.rowData &&
      this.rowData.TAX_YEAR &&
      this.rowData.SCENARIO &&
      this.rowData.JCD_KEY &&
      this.rowData.COMBINATION_KEY &&
      this.rowData.SUB_FORM
    );
  }

  private parseSubForm(subFormValue: string): any {
    const [formName = '', schdName = '', partName = '', sectionName = ''] =
      (subFormValue || '').split('~');

    return {
      formName,
      schdName,
      partName,
      sectionName,
      subForm: formName,
    };
  }

  private buildRequestContext(): any {
    return {
      activity_key: this.filterParams?.activity_key,
      screen_key: this.filterParams?.screen_key || this.rowData?.SCREEN_KEY,
      screen_type: this.filterParams?.screen_type,
      jcd_ta_key: this.filterParams?.jcd_ta_key,
      filing_key: this.filterParams?.filing_key,
      filter_by: this.filterParams?.filter_by,
      parent_me_string: this.filterParams?.parent_me_string,
      is_locked: this.filterParams?.is_locked,
      issue_id: this.filterParams?.issue_id,
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save,
      issue_short_desc: this.filterParams?.issue_short_desc,
      scenarioCode: this.filterParams?.scenarioCode,
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled,
    };
  }

  private withDefinedParams(params: any): any {
    return Object.keys(params).reduce((acc: any, key: string) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private mapSelectedRow(selection: any, order: number): any {
    const row = selection?.data && typeof selection.data === 'object' ? selection.data : selection;
    const parsed = this.parseSubForm(row?.SUB_FORM || '');

    return {
      entityName: row?.LE_NAME || '',
      taxCode: row?.CDR_NO || row?.TAX_CODE || '',
      meName: row?.ME_NAME || '',
      taxYearBegin: row?.TAX_YEAR_BEGIN || '',
      taxYearEnd: row?.TAX_YEAR_END || '',
      meCode: row?.ME_CODE || '',
      combination_key: row?.COMBINATION_KEY,
      formName: parsed.formName,
      schdName: parsed.schdName,
      partName: parsed.partName,
      sectionName: parsed.sectionName,
      subForm: parsed.subForm,
      order,
    };
  }

  private setupComponentData() {
    this.curentyear = this.rowData.TAX_YEAR || '';

    const parsedSubForm = this.parseSubForm(this.rowData.SUB_FORM || '');
    this.formDataObj = {
      entityName: this.rowData.LE_NAME || '',
      taxYearBegin: this.rowData.TAX_YEAR_BEGIN || '',
      taxYearEnd: this.rowData.TAX_YEAR_END || '',
      meCode: this.rowData.ME_CODE || '',
      meName: this.rowData.ME_NAME || '',
      taxCode: this.rowData.CDR_NO || this.rowData.TAX_CODE || '',
      formName: parsedSubForm.formName,
      schdName: parsedSubForm.schdName,
      partName: parsedSubForm.partName,
      sectionName: parsedSubForm.sectionName,
      subForm: parsedSubForm.subForm,
      combination_key: this.rowData.COMBINATION_KEY,
    };

    this.batchSelect = this.selectedRows.length > 1;

    if (this.batchSelect) {
      const uniqueSelections = new Map<string, any>();
      this.selectedRows.forEach((selection: any, index: number) => {
        const mapped = this.mapSelectedRow(selection, index);
        if (mapped.formName && !uniqueSelections.has(mapped.formName)) {
          uniqueSelections.set(mapped.formName, mapped);
        }
      });
      this.defaultsMultipleSelected = Array.from(uniqueSelections.values());
    } else if (this.rowData.DEFAULTS_MULTIPLE_SELECTED && this.rowData.DEFAULTS_MULTIPLE_SELECTED.length > 0) {
      this.defaultsMultipleSelected = this.rowData.DEFAULTS_MULTIPLE_SELECTED;
    } else {
      this.defaultsMultipleSelected = [
        {
          subForm: parsedSubForm.subForm,
          formName: parsedSubForm.formName,
          schdName: parsedSubForm.schdName,
          partName: parsedSubForm.partName,
          sectionName: parsedSubForm.sectionName,
          order: 0,
        }
      ];
    }

    if (this.defaultsMultipleSelected.length > 0) {
      this.activeTab = this.defaultsMultipleSelected[0].subForm;
    }
  }

  initializeComponent() {
    this.fetchModalCalcData();
    this.fetchModalData1();
    this.fetchLOVF4136();
  }

  fetchModalData1(order?: number) {
    this.loadingData = true;
    
    if (this.batchSelect) {
      if (this.allTabsList != null && this.allTabsList.length > 0) {
        const subFormAlreadyExists = this.allTabsList.filter((item: any) => 
          item === this.activeTab
        );
        
        if (subFormAlreadyExists.length !== 0) {
          const subFormOutterAlreadyExists = this.allTablsOutterList.filter((item: any) => 
            item === subFormAlreadyExists[0][0].ATTRIB_SHORT_CODE
          );
          const currentFormHeader = this.allTabsFormHeader.filter((item: any) => 
            item === subFormAlreadyExists[0][0].FORM_NAME
          );
          const currentTableHeader = this.allTabsTableHeader.filter((item: any) => 
            item === subFormAlreadyExists[0][0].FORM_NAME
          );
        }
      }

      if (this.currentTabs.length !== 0) {
        const currentTabExists = this.currentTabs.filter((tab: string) => 
          tab === this.activeTab
        );
        
        if (!currentTabExists || currentTabExists.length === 0) {
          this.currentTabs.push(this.activeTab);
        }
      }
    }

    // Fetch form data based on active tab
    const params = this.buildFetchParams();
    
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject', 
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.processFormData(data);
      this.loadingData = false;
      this.cdr.markForCheck();
    });
  }

  private buildFetchParams() {
    let fm, sn, pn, sm;
    
    if (this.batchSelect) {
      const selected = this.defaultsMultipleSelected.find(item => 
        item.subForm === this.activeTab
      ) || this.defaultsMultipleSelected[0];
      
      fm = selected.formName;
      sn = selected.schdName;
      pn = selected.partName;
      sm = selected.sectionName;
    } else {
      fm = this.formDataObj.formName;
      sn = this.formDataObj.schdName;
      pn = this.formDataObj.partName;
      sm = this.formDataObj.sectionName;
    }

    return this.withDefinedParams({
      "action_code": 'hfyq9t',
      "sso_id": this.logged_in_user,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "combination_key": this.rowData.COMBINATION_KEY,
      "form_no": fm,
      "schedule_name": sn,
      "part_no": pn,
      "section_name": sm,
      "issue_key": 0,
      ...this.buildRequestContext(),
    });
  }

  private processFormData(data: any) {
    this.modalTitle = data.jsonObject[0]?.ATTRIB_DESCRIPTION || 'Edit Credit Forms';
    this.formHeader = data.jsonObject.filter((item: any) => item.ROW_TYPE === "FH");
    this.tableHeader = data.jsonObject.filter((item: any) => item.ROW_TYPE === "TH");
    this.list = data.jsonObject.slice(1);

    this.formList = this.list.filter((obj: any) => obj.ROW_TYPE !== 'TH');

    // Build outerList for unique form lines
    this.outerList = [];
    for (let i = 0; i < this.formList.length; i++) {
      if (this.formList[i].FORM_LINE_KEY != null) {
        let tempCheck1: any[] = [];
        if (this.outerList != null && this.outerList.length > 0) {
          tempCheck1 = this.outerList.filter((item: any) => 
            item.FORM_LINE_KEY === this.formList[i].FORM_LINE_KEY && 
            item.IS_FORM_DISPLAY === 'Y'
          );
        }

        // Handle date fields
        if (this.formList[i].EDIT_TYPE === 'custom_date_various' && 
            this.formList[i].ATTRIB_VALUE !== 'VARIOUS') {
          this.formList[i].ATTRIB_VALUE = new Date(this.formList[i].ATTRIB_VALUE);
          this.handleDateValidations(this.formList[i]);
        }

        if (!tempCheck1 || tempCheck1.length === 0) {
          this.outerList.push(this.formList[i]);
        }
      }
    }

    // Store data in batch arrays
    this.allTabsFormHeader.push(this.formHeader);
    this.allTabsTableHeader.push(this.tableHeader);
    this.allTabsList.push(_.cloneDeep(this.formList));
    this.allTablsOutterList.push(_.cloneDeep(this.outerList));

    // Store original data
    this.originalTabsList.push(_.cloneDeep(this.formList));
    this.originalTablsOutterList.push(_.cloneDeep(this.outerList));
  }

  private handleDateValidations(formItem: any) {
    const formula = formItem.ATTRIB_CALC_FORMULA;
    if (formula && formula.substring(0, 4) === 'json') {
      const charLength = 'json'.length;
      const subFormula = safeJsonInput<any>(formula.slice(charLength), null, 'f3800-piii-consol.parse1');
      
      // Handle default from date
      if (subFormula[0].DEFAULT_FROM_DATE === 'CURR_RP_BEGIN') {
        formItem.taxYearBeginDate = new Date(formItem.TAX_YEAR_BEGIN);
      } else if (subFormula[0].DEFAULT_FROM_DATE === 'CURR_TY_BEGIN') {
        formItem.taxYearBeginDate = new Date(`01/01/${this.curentyear}`);
      } else {
        formItem.taxYearBeginDate = new Date(subFormula[0].DEFAULT_FROM_DATE);
      }

      // Handle default to date
      if (subFormula[1].DEFAULT_TO_DATE === 'CURR_RP_END') {
        formItem.taxYearEndDate = new Date(formItem.TAX_YEAR_END);
      } else if (subFormula[1].DEFAULT_TO_DATE === 'CURR_TY_END') {
        formItem.taxYearEndDate = new Date(`12/31/${this.curentyear}`);
      } else {
        formItem.taxYearEndDate = this.formDataObj.taxYearEndDate;
      }
    }
  }

  fetchLOVF4136(order?: number) {
    const params = {
      "action_code": 'sjh60s',
      "sso_id": this.logged_in_user,
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "form_no": this.activeTab
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data && data.jsonObject) {
        this.formLOV = data.jsonObject;
      }
      this.cdr.markForCheck();
    });
  }

  fetchModalCalcData(order?: number) {
    let fm, sn, pn, sm;
    
    if (this.batchSelect) {
      if (order == null) {
        fm = this.defaultsMultipleSelected[0].formName;
        sn = this.defaultsMultipleSelected[0].schdName;
        pn = this.defaultsMultipleSelected[0].partName;
        sm = this.defaultsMultipleSelected[0].sectionName;
      } else {
        fm = this.defaultsMultipleSelected[order].formName;
        sn = this.defaultsMultipleSelected[order].schdName;
        pn = this.defaultsMultipleSelected[order].partName;
        sm = this.defaultsMultipleSelected[order].sectionName;
      }
    } else {
      fm = this.formDataObj.formName;
      sn = this.formDataObj.schdName;
      pn = this.formDataObj.partName;
      sm = this.formDataObj.sectionName;
    }

    const params = {
      "action_code": '1ubsuu',
      "tax_year": this.rowData.TAX_YEAR,
      "scenario": this.rowData.SCENARIO,
      "jcd_key": this.rowData.JCD_KEY,
      "form_no": this.activeTab,
      "schedule_name": sn,
      "part_no": pn,
      "section_name": sm
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.formCalcData = data.jsonObject;
      this.cdr.markForCheck();
    });
  }

  validateSubForm(row: any) {
    if (row && row.DATA_TYPE === 'number') {
      for (let g = 0; g < this.formCalcData.length; g++) {
        this.calcAmt(
          this.formCalcData[g].ATTRIB_CALC_FORMULA,
          this.formCalcData[g].LINE_NO,
          this.formCalcData[g].LINE_ATTRIB_KEY,
          this.formCalcData[g].ATTRIB_COL,
          this.formCalcData[g].EDIT_TYPE
        );
      }
    } else if (row) {
      row.IS_CHANGED_FLAG = 'Y';
    }
  }

  calcAmt(formula: string, line_no: string, attrib_key: string, attrib_col: string, edit_type: string) {
    // Implementation of calculation logic from original controller
    let currentFormula = formula;
    
    try {
      const calculatedVal = safeEvalFormula(currentFormula, null, 'f3800-piii-consol');
      if (calculatedVal !== null && typeof calculatedVal !== 'undefined') {
        const changedFormulaAttribVal = this.formList.filter((item: any) => 
          item.LINE_NO === line_no && item.LINE_ATTRIB_KEY === attrib_key
        );
        
        if (changedFormulaAttribVal.length > 0) {
          changedFormulaAttribVal[0].ATTRIB_VALUE = calculatedVal.toFixed();
          changedFormulaAttribVal[0].IS_CHANGED_FLAG = 'Y';
        }
      }
    } catch (e) {
      console.error('Error in calculation:', e);
    }
  }

  checkBoxHandler(formLineKey: string, lineAttribKey: string, attribCol: string) {
    // Handle checkbox interactions for radio groups
    const relatedItems = this.formList.filter((item: any) => 
      item.FORM_LINE_KEY === formLineKey && item.EDIT_TYPE === 'radio'
    );
    
    relatedItems.forEach((item: any) => {
      if (item.LINE_ATTRIB_KEY !== lineAttribKey) {
        item.ATTRIB_VALUE = 'false';
      }
      item.IS_CHANGED_FLAG = 'Y';
    });
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    const returnClobSettingsObj: any = {};
    const creditFormSaveDtls: any[] = [];

    // Process all tabs data for batch save
    for (let key in this.allTabsList) {
      const saveData = this.allTabsList[key].filter((item: any) => 
        item.IS_CHANGED_FLAG === 'Y'
      );

      for (let i = 0; i < saveData.length; i++) {
        const returnObj: any = {};
        returnObj['form_name'] = saveData[i].formName;
        returnObj['combination_key'] = saveData[i].COMBINATION_KEY;
        returnObj['part_no'] = saveData[i].partName;
        returnObj['schd_no'] = saveData[i].schdName;
        returnObj['section_name'] = saveData[i].sectionName;
        returnObj['form_key'] = saveData[i].FORM_KEY;
        returnObj['form_line_key'] = saveData[i].FORM_LINE_KEY;
        returnObj['line_attrib_key'] = saveData[i].LINE_ATTRIB_KEY;
        returnObj['attrib_type'] = saveData[i].ATTRIB_TYPE;
        returnObj['attrib_prop_key'] = saveData[i].ATTRIB_PROP_KEY;
        returnObj['acct_ref_key'] = saveData[i].ACCT_REF_KEY;

        if (saveData[i].EDIT_TYPE === 'custom_date_various' && 
            saveData[i].ATTRIB_VALUE !== 'VARIOUS') {
          const formattedDate = this.formatDate(saveData[i].ATTRIB_VALUE);
          returnObj['attrib_value'] = formattedDate;
        } else {
          returnObj['attrib_value'] = saveData[i].ATTRIB_VALUE;
        }

        returnObj['trans_details_key'] = saveData[i].TRANS_DETAILS_KEY;
        returnObj['trans_type_key'] = saveData[i].TRANS_TYPE_KEY;
        returnObj['is_changed_flag'] = saveData[i].IS_CHANGED_FLAG;
        returnObj['occurrence'] = saveData[i].OCCURRENCE ? 
          parseInt(saveData[i].OCCURRENCE) : 1;

        creditFormSaveDtls.push(returnObj);
      }
    }

    if (creditFormSaveDtls.length === 0) {
      this.gtwSnackbar.openSnackBar("No changes to save.", 'gtw-snackbar--yellow', true, 4);
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }

    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['screen_key'] = this.rowData.SCREEN_KEY || this.globalService?.globalParams?.screen_key || '';
    returnClobSettingsObj['form_name'] = this.formDataObj.formName;
    returnClobSettingsObj['combination_key'] = this.formDataObj.combination_key;

    this.sendDetails(returnClobSettingsObj, creditFormSaveDtls, 
      'Credits has been successfully saved/updated');
  }

  private sendDetails(returnClobSettingsObj: any, creditFormSaveDtls: any[], message: string) {
    this.f3800PIIIConsolService.saveCreditForms(
      this.GENERAL_CONFIG.base_url, 
      returnClobSettingsObj, 
      creditFormSaveDtls, 
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
        } else {
          // Show success snackBar
          this.gtwSnackbar.openSnackBar(message, 'gtw-snackbar--green', true, 4);
          
          // Emit using @Output (AngularJS will close modal automatically)
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.markForCheck();
      }
    );
  }

  reset() {
    // Reset current tab data to original state
    const currentTabIndex = this.currentTabs.indexOf(this.activeTab);
    if (currentTabIndex >= 0 && this.originalTabsList[currentTabIndex]) {
      this.allTabsList[currentTabIndex] = _.cloneDeep(this.originalTabsList[currentTabIndex]);
      this.allTablsOutterList[currentTabIndex] = _.cloneDeep(this.originalTablsOutterList[currentTabIndex]);
      
      // Update current display data
      this.formList = this.allTabsList[currentTabIndex];
      this.outerList = this.allTablsOutterList[currentTabIndex];
    }
  }

  cancel() {
    // Emit cancel event to close modal (AngularJS integration)
    this.requestGridUpdate.emit('cancel');
  }

  cancelForm() {
    if (!this.batchSelect) {
      this.cancel();
    } else {
      if (this.defaultsMultipleSelected.length === 1) {
        this.cancel();
      } else {
        // Remove current form from batch selection
        for (let i = this.defaultsMultipleSelected.length - 1; i >= 0; --i) {
          if (this.defaultsMultipleSelected[i].subForm === this.activeTab) {
            this.defaultsMultipleSelected.splice(i, 1);
          }
        }
        
        if (this.defaultsMultipleSelected && this.defaultsMultipleSelected.length > 0) {
          this.activeTab = this.defaultsMultipleSelected[0].subForm;
        }
        
        this.fetchModalCalcData();
        this.fetchModalData1();
      }
    }
  }

  onTabChange(tabItem: any) {
    this.activeTab = tabItem.subForm;
    
    // Call appropriate data fetching based on form type
    if (this.activeTab === 'F8912' || this.activeTab === 'F8820' || this.activeTab === 'F8941') {
      // Special handling for specific forms
      this.showSpecialFormNotice();
      this.fetchSpecialFormData(tabItem);
    } else {
      // Standard form data fetching
      this.fetchModalData1(tabItem.order);
    }
  }

  private fetchSpecialFormData(tabItem: any) {
    // Specific data fetching logic for F8912, F8820, F8941
    console.log(`Loading special form data for ${this.activeTab}`);
    
    // This would typically involve different API calls or data processing
    // For now, we'll use the standard flow but could be customized
    this.fetchModalData1(tabItem.order);
    
    // Additional specific processing could be added here
    switch (this.activeTab) {
      case 'F8912':
        this.handleF8912SpecificLogic();
        break;
      case 'F8820':
        this.handleF8820SpecificLogic();
        break;
      case 'F8941':
        this.handleF8941SpecificLogic();
        break;
    }
  }

  private handleF8912SpecificLogic() {
    // F8912 specific initialization
    console.log('Initializing F8912 specific features');
  }

  private handleF8820SpecificLogic() {
    // F8820 specific initialization
    console.log('Initializing F8820 specific features');
  }

  private handleF8941SpecificLogic() {
    // F8941 specific initialization
    console.log('Initializing F8941 specific features');
  }

  private formatDate(date: any): string {
    if (!date) return '';
    // Format date as MM/dd/yy
    const d = new Date(date);
    return (d.getMonth() + 1).toString().padStart(2, '0') + '/' + 
           d.getDate().toString().padStart(2, '0') + '/' + 
           d.getFullYear().toString().substr(-2);
  }

  // Track by function for ngFor
  itemTracker(index: number, item: any): any {
    return item ? item.LINE_ATTRIB_KEY || item.subForm || index : index;
  }

  // Filter function for form display (kept for potential custom filtering)
  filterFormDisplay(item: any): boolean {
    return item.IS_FORM_DISPLAY === 'Y';
  }

  // Checkbox change handler
  onCheckboxChange(event: Event, row: any): void {
    const target = event.target as HTMLInputElement;
    if (target) {
      row.ATTRIB_VALUE = target.checked ? 'true' : 'false';
      this.validateSubForm(row);
    }
  }

  // Radio checkbox change handler
  onRadioCheckboxChange(event: Event, row: any): void {
    const target = event.target as HTMLInputElement;
    if (target) {
      row.ATTRIB_VALUE = target.checked ? 'true' : 'false';
      this.checkBoxHandler(row.FORM_LINE_KEY, row.LINE_ATTRIB_KEY, row.ATTRIB_COL);
    }
  }
} 